/**
 * Figma Clipboard Scene Parser
 *
 * A strongly-typed parser for Figma's internal scene graph format (fig-kiwie).
 * Preserves Figma's internal structure rather than transforming to REST API format.
 *
 * @example
 * ```typescript
 * import { parseClipboardScene, parseFirstChild } from './clipboard-parser'
 *
 * // Parse entire scene
 * const scene = parseClipboardScene(jsonData)
 * console.log(scene.metadata.fileKey)
 *
 * // Parse just the first child
 * const node = parseFirstChild(jsonData)
 * console.log(node?.name, node?.type)
 * ```
 */

export * from './types/index.js'
export * from './types/component-tree.js'
export { ClipboardSceneParser, type ValidationResult } from './parser.js'
export {
  ComponentTreeTransformer,
  transformClipboard,
  type TransformOptions,
} from './transformer.js'
export {
  buildVariableLookupMap,
  collectNodeHints,
  extractAssetRefKeys,
  extractAssetRefKeysSimple,
  type ResolvedVariable,
  type VariableLookupMap,
  type HintUsage,
} from './utils/hint-resolver.js'
export { componentTreeToPrompt, TAILWIND_INSTRUCTIONS } from './utils/component-tree-to-prompt.js'
export type {
  ComponentPropAssignment,
  ComponentPropAssignmentValue,
  ComponentPropTextValue,
} from './types/properties/instance.js'

import { ClipboardSceneParser } from './parser.js'
import type {
  ClipboardParseResult,
  FigmaInternalScene,
  SceneNode,
  VariableRegistry,
} from './types/scene.js'

const _parser = new ClipboardSceneParser()

/**
 * Check if a node is visible
 *
 * @param node - The node to check
 * @returns True if the node is visible (visible is undefined or true)
 */
export function isNodeVisible(node: SceneNode): boolean {
  return _parser.isVisible(node)
}

/**
 * Parse clipboard JSON data into a strongly-typed scene structure
 *
 * @param jsonData - The clipboard JSON data (either as object or string)
 * @returns Parsed and typed scene structure
 * @throws Error if the data is invalid
 */
export function parseClipboardScene(jsonData: unknown): FigmaInternalScene {
  let data: unknown

  if (typeof jsonData === 'string') {
    data = JSON.parse(jsonData)
  } else {
    data = jsonData
  }

  // Basic structure validation
  if (!data || typeof data !== 'object') {
    throw new Error('Invalid clipboard data: expected object')
  }

  const scene = data as FigmaInternalScene

  if (!scene.metadata) {
    throw new Error('Invalid clipboard data: missing metadata')
  }

  if (!scene.root) {
    throw new Error('Invalid clipboard data: missing root node')
  }

  return scene
}

/**
 * Convenience function to parse the first child from clipboard data
 *
 * This extracts: root (DOCUMENT) -> children[0] (CANVAS) -> children[0] (first item)
 *
 * @param jsonData - The clipboard JSON data (either as object or string)
 * @param filterHidden - If true, skip hidden nodes (default: false)
 * @returns The first child node, or null if not found
 */
export function parseFirstChild(
  jsonData: unknown,
  filterHidden = false,
): SceneNode | null {
  const parser = new ClipboardSceneParser()
  const scene = parseClipboardScene(jsonData)
  return parser.parseFirstChild(scene, filterHidden)
}

/**
 * Parse variable registry from clipboard data
 *
 * This extracts variables and variable sets from the "Internal Only Canvas"
 *
 * @param jsonData - The clipboard JSON data (either as object or string)
 * @returns Variable registry or null if "Internal Only Canvas" not found
 */
export function parseVariableRegistry(jsonData: unknown): VariableRegistry | null {
  const parser = new ClipboardSceneParser()
  const scene = parseClipboardScene(jsonData)
  return parser.parseVariableRegistry(scene)
}

/**
 * Parse both component and variable registry from clipboard
 *
 * This extracts both the first child component and the variable registry
 *
 * @param jsonData - The clipboard JSON data (either as object or string)
 * @param filterHidden - If true, skip hidden nodes in component (default: false)
 * @returns Parse result with component and variable registry
 */
export function parseClipboard(
  jsonData: unknown,
  filterHidden = false,
): ClipboardParseResult {
  const parser = new ClipboardSceneParser()
  const scene = parseClipboardScene(jsonData)
  return parser.parseClipboard(scene, filterHidden)
}
